/**
 * QMP-FE-7 (F3) — WHAT EACH COMPANY HAS EVER SAID ABOUT ONE PACKAGE (§16).
 *
 * `GET /qmp/packages/{qwp_id}/approvals` -> `{qwp_id, records[]}`, every
 * revision, the newest first. Read when the package is opened (the section
 * lives inside the open row), and read again when `refreshKey` moves — the
 * page re-reads the plan after every decision, and the key is built from what
 * that read served, so the history follows the record instead of guessing it.
 *
 * `state`:
 *   * `loading` — nothing is drawn yet;
 *   * `ok`      — `records` is what the server served;
 *   * `hidden`  — 404: outside the fence the section does not exist;
 *   * `error`   — 503 and anything else: `message` + `retry()`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getQmpPackageApprovals } from '../lib/api';
import { describeHistoryError } from '../lib/qmpApprovals';

const LOADING = { state: 'loading', records: [], message: '' };

export default function useQmpPackageApprovals(projectId, qwpId, refreshKey) {
  const [result, setResult] = useState(LOADING);
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!projectId || !qwpId) { setResult({ ...LOADING, state: 'hidden' }); return undefined; }
    seq.current += 1;
    const mine = seq.current;
    getQmpPackageApprovals(projectId, qwpId).then((res) => {
      if (mine !== seq.current) return;
      const records = (res && res.data && res.data.records) || [];
      setResult({ state: 'ok', records, message: '' });
    }).catch((err) => {
      if (mine !== seq.current) return;
      const failure = describeHistoryError(err);
      setResult({
        state: failure.kind === 'hidden' ? 'hidden' : 'error',
        records: [],
        message: failure.message,
      });
    });
    return () => { seq.current += 1; };
  }, [projectId, qwpId, refreshKey, attempt]);

  const retry = useCallback(() => {
    setResult(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return { ...result, retry };
}
